/**
 * Where the API lives (§2.1).
 *
 * GitHub Pages serves the SPA from origin A; the game server is origin B. The base
 * URL is baked in at build time — staging and production are separate builds, not
 * a runtime toggle — and everything else is derived from it so there is exactly
 * one place a wrong host can come from.
 */

export class ApiConfigError extends Error {}

/**
 * Derives the WebSocket URL from the API base URL, upgrading the scheme with it.
 *
 * The scheme upgrade is the point: Pages is HTTPS, so an `ws://` socket from an
 * `https://` page is blocked as mixed content, exactly like an `http://` fetch
 * would be. Deriving rather than configuring means the two can never disagree.
 */
export function deriveWsUrl(apiBaseUrl: string, path = '/ws'): string {
  let url: URL;
  try {
    url = new URL(apiBaseUrl);
  } catch {
    throw new ApiConfigError(`VITE_API_BASE_URL is not a valid URL: ${JSON.stringify(apiBaseUrl)}`);
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new ApiConfigError(
      `VITE_API_BASE_URL must be http or https, got ${JSON.stringify(url.protocol)}`,
    );
  }

  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = joinPath(url.pathname, path);
  url.search = '';
  url.hash = '';
  return url.toString();
}

export function apiUrl(apiBaseUrl: string, path: string): string {
  const url = new URL(apiBaseUrl);
  url.pathname = joinPath(url.pathname, path);
  return url.toString();
}

function joinPath(base: string, path: string): string {
  const left = base.endsWith('/') ? base.slice(0, -1) : base;
  const right = path.startsWith('/') ? path : `/${path}`;
  return `${left}${right}`;
}

export interface ApiConfig {
  readonly baseUrl: string;
  readonly wsUrl: string;
}

export function readApiConfig(env: ImportMetaEnv): ApiConfig {
  const baseUrl = env.VITE_API_BASE_URL;
  if (!baseUrl) {
    throw new ApiConfigError(
      'VITE_API_BASE_URL was not set at build time. The SPA has no way to find the game server (§2.1).',
    );
  }
  return { baseUrl: baseUrl.replace(/\/$/, ''), wsUrl: deriveWsUrl(baseUrl) };
}
